import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * SearchableSelect — single-select dropdown with a filter box.
 *
 * Mirrors the trigger styling of `MultiSelectDropdown` so the two feel
 * like siblings in forms that mix single- and multi-select. Use this
 * anywhere a plain <select> would work fine with 5 options but starts
 * hurting once the list grows past ~15 (site pickers, guard pickers,
 * client pickers).
 *
 * Props:
 *   options       [{ value, label, hint? }]  — hint renders as dim
 *                                              secondary text under label
 *   value         string | ""                 — current selected value
 *   onChange      (nextValue: string) => void
 *   placeholder   string                     — shown when value === ""
 *   allowClear    boolean                    — render an "x" to reset
 *   disabled      boolean
 *   triggerClassName / testId                — customization escape hatches
 *   emptyHint     string                     — no-matches empty state
 */
export default function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Select…",
  allowClear = true,
  disabled = false,
  triggerClassName = "w-full",
  testId,
  emptyHint = "No matches",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(q) ||
        (o.hint || "").toLowerCase().includes(q) ||
        (o.value || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  // Close on outside click. Keyboard `Esc` also closes via input onKeyDown.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Auto-focus the search box when the popover opens so the user can
  // start typing immediately — this is the whole point.
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const pick = (v) => {
    onChange?.(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        className={`inline-flex items-center justify-between gap-2 ${triggerClassName} bg-[#0F0F12] border border-[#27272A] hover:border-zinc-500 text-zinc-200 text-xs uppercase tracking-widest px-3 py-2 transition-colors duration-150 ${open ? "border-[#FF3B30]" : ""} ${disabled ? "opacity-50 cursor-not-allowed hover:border-[#27272A]" : ""}`}
      >
        <span className={`truncate normal-case tracking-normal ${selected ? "text-zinc-100" : "text-zinc-500"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {allowClear && selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              data-testid={testId ? `${testId}-clear` : undefined}
              onClick={(e) => { e.stopPropagation(); pick(""); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault(); e.stopPropagation(); pick("");
                }
              }}
              className="p-0.5 hover:text-[#FF3B30] cursor-pointer"
              aria-label="Clear selection"
            >
              <X className="size-3" />
            </span>
          )}
          <ChevronDown className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full min-w-[240px] bg-[#0A0A0A] border border-[#27272A] shadow-2xl"
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          <div className="p-2 border-b border-[#27272A] flex items-center gap-2">
            <Search className="size-3.5 text-zinc-500 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
                if (e.key === "Enter" && filtered.length > 0) {
                  e.preventDefault();
                  pick(filtered[0].value);
                }
              }}
              placeholder="Search…"
              className="flex-1 bg-transparent text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="text-zinc-500 hover:text-zinc-200"
                aria-label="Clear search"
              >
                <X className="size-3" />
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[11px] text-zinc-500">
                {emptyHint}
              </div>
            ) : (
              filtered.map((o) => {
                const isSelected = o.value === value;
                return (
                  <button
                    key={o.value || "__empty__"}
                    type="button"
                    onClick={() => pick(o.value)}
                    className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                      isSelected
                        ? "bg-[#0A84FF]/15 text-[#7CC4FF]"
                        : "text-zinc-300 hover:bg-[#0F0F12] hover:text-white"
                    }`}
                    data-testid={testId ? `${testId}-option-${o.value || "empty"}` : undefined}
                  >
                    <div className="truncate">{o.label}</div>
                    {o.hint && (
                      <div className="text-[10px] text-zinc-500 truncate font-mono">{o.hint}</div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
